import { useFpCampaignCartService } from '@cart-services/hooks/useFpCampaignCartService'
import { useFpCampaignCoupons } from '@cart-services/hooks/useFpCampaignCoupons'
import { useFpCampaignProductsInfo } from '@cart-services/hooks/useFpCampaignProductsInfo'
import { useFpCategoryPromotionStore } from '@cart-services/hooks/useFpCategoryPromotionStore'
import { sortCoupons } from '@cart-services/utils/sortCoupons'
import { CarouselCategoryPromotion } from '@cart-ui/CategoryPromotion/CarouselCategoryPromotion'
import { CategoryPromotionAnnouncement } from '@cart-ui/CategoryPromotion/CategoryPromotionAnnouncement'
import { CategoryPromotionImages } from '@cart-ui/CategoryPromotion/CategoryPromotionImages'
import { parseCouponSpecificProductsMap } from '@cart-ui/PromotionCouponList/parseCouponSpecificProductsMap'
import { PromotionCouponList } from '@cart-ui/PromotionCouponList/PromotionCouponList'
import { sendFastpassAddToCartEvent } from '@dni-analysis'
import { generateItemVariant } from '@dni-analysis/events/utils'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useApiCampaignActivityInfo } from '@hooks-api/fortune/useApiCampaignActivityInfo'
import { useApiCampaignBanners } from '@hooks-api/fortune/useApiCampaignBanners'
import { useApiCouponsSpecificProducts } from '@hooks-api/fortune/useApiCouponsSpecificProducts'
import { type ECommerceProduct } from '@hooks-api/types/dataModel/products'
import { VStack } from '@pets-ui/VStack/VStack'

import { useMemo } from 'react'
import { useProductCatalog } from '../ProductCatalogProvider/useProductCatalog'

export const CategoryPromotion = (props: { scrollMarginTop?: number }) => {
  const { scrollMarginTop = 27 } = props
  const { focusCouponId, setFocusCouponId } = useFpCategoryPromotionStore()
  const { campaignId, countryCode, currency, campaignCode, projectCode } = useProductCatalog()
  const { items: cartItems, upsertCartItem } = useFpCampaignCartService(campaignId)

  const { destructuredData: exhibitorSettings } = useApiCampaignActivityInfo(campaignCode)
  const { availableMergedCoupons } = useFpCampaignCoupons(campaignId, countryCode, campaignCode)
  const { destructuredData: couponSpecificProducts } = useApiCouponsSpecificProducts({
    campaign_code: campaignCode,
    coupon_ids: availableMergedCoupons?.map(coupon => coupon.coupon_id),
  })
  const specificProductIds = useMemo(() => [...new Set(
    couponSpecificProducts?.flatMap(product => product.product_groups.flatMap(group => group.map(item => ({
      product_id: item.product_id,
      project_code: item.project_code,
    })))),
  )], [couponSpecificProducts])

  const { productMap } = useFpCampaignProductsInfo({ campaignId, campaignCode, countryCode, extraProductIds: specificProductIds })
  const { destructuredData: campaignBanners } = useApiCampaignBanners({
    campaign_code: campaignCode,
  })

  const couponSpecificProductsMap = useMemo(() => {
    if (!couponSpecificProducts || !productMap) return new Map()
    return parseCouponSpecificProductsMap(couponSpecificProducts, productMap)
  }, [couponSpecificProducts, productMap])

  const handleFocusEnd = useCallbackEvent(() => {
    setFocusCouponId(null)
  })

  const handleQuantityChange = useCallbackEvent((product: ECommerceProduct, quantity?: number) => {
    if (!product.product_id || !product.sku || quantity === undefined) return
    const currentItem = cartItems.find(item => item.cartItemId === product.product_id)
    const isItemAdded = (currentItem?.quantity || 0) < quantity
    if (isItemAdded) {
      sendFastpassAddToCartEvent({
        campaign_code: campaignCode,
        currency,
        value: Number(product.sale_price || 0),
        items: [{
          currency,
          item_name: product.name,
          item_id: String(product.product_id ?? ''),
          price: Number(product.sale_price || 0),
          quantity,
          item_brand: projectCode,
          item_variant: generateItemVariant({
            productName: product.name,
            attributes: product.attributes,
          }),
        }],
      })
    }

    upsertCartItem({
      cartItemId: product.product_id,
      product_id: product.parent_product_id,
      sku: product.sku,
      project_code: product.project_code,
      quantity,
      delivery_class: product.delivery_class,
    })
  })
  const sortedCoupons = useMemo(() => availableMergedCoupons ? sortCoupons(availableMergedCoupons) : undefined, [availableMergedCoupons])

  if (!exhibitorSettings?.has_homepage) return null
  return (
    <VStack gap={3}>
      <CarouselCategoryPromotion campaignBanners={campaignBanners} />
      <CategoryPromotionImages campaignBanners={campaignBanners} />
      <CategoryPromotionAnnouncement
        title="最新公告"
        description={exhibitorSettings?.announcement}
      />
      <PromotionCouponList
        coupons={sortedCoupons}
        couponSpecificProductsMap={couponSpecificProductsMap}
        currency={currency}
        focusCouponId={focusCouponId}
        onFocusEnd={handleFocusEnd}
        cartItems={cartItems}
        onQuantityChange={handleQuantityChange}
        scrollMarginTop={scrollMarginTop}
        isShowOriginalPrice
      />
    </VStack>
  )
}
